// src/utils/dateUtils.ts

/**
 * 日期处理工具类
 * 专门处理中国时区（UTC+8）的日期格式化和转换
 */
export class DateUtils {
  /**
   * 将日期字符串转换为带有中国时区的ISO 8601格式
   * @param dateString 日期字符串，格式为 "YYYY-MM-DD"
   * @param isEndOfDay 是否设置为当天结束时间 (23:59:59.999)，否则为开始时间 (00:00:00.000)
   * @returns ISO 8601格式的日期时间字符串，带有 +08:00 时区标识
   */
  static toChineseISOString(
    dateString: string,
    isEndOfDay: boolean = false
  ): string {
    if (!dateString) return "";

    // 直接使用选择的日期，添加时间部分和时区标识
    if (isEndOfDay) {
      // 当天结束时间 23:59:59.999
      return `${dateString}T23:59:59.999+08:00`;
    } else {
      // 当天开始时间 00:00:00.000
      return `${dateString}T00:00:00.000+08:00`;
    }
  }

  /**
   * 将日期对象格式化为 YYYY-MM-DD 格式
   * @param date 日期对象
   * @returns 格式化后的日期字符串
   */
  static formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * 将日期对象格式化为 YYYY-MM-DD HH:MM:SS 格式
   * @param date 日期对象
   * @returns 格式化后的日期时间字符串
   */
  static formatDateTime(date: Date): string {
    const dateStr = this.formatDate(date);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${dateStr} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * 获取当前日期的字符串，格式为 YYYY-MM-DD
   * @returns 当前日期字符串
   */
  static getCurrentDate(): string {
    return this.formatDate(new Date());
  }

  /**
   * 获取今天开始时间的ISO字符串（中国时区）
   * @returns 今天开始时间的ISO字符串
   */
  static getTodayStartISOString(): string {
    return this.toChineseISOString(this.getCurrentDate(), false);
  }

  /**
   * 获取今天结束时间的ISO字符串（中国时区）
   * @returns 今天结束时间的ISO字符串
   */
  static getTodayEndISOString(): string {
    return this.toChineseISOString(this.getCurrentDate(), true);
  }

  /**
   * 获取相对于今天的日期
   * @param days 相对天数，正数为未来，负数为过去
   * @returns 日期字符串，格式为 YYYY-MM-DD
   */
  static getRelativeDate(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return this.formatDate(date);
  }

  /**
   * 解析ISO日期字符串为本地日期对象
   * @param isoString ISO格式的日期字符串
   * @returns 日期对象
   */
  static parseISOString(isoString: string): Date {
    return new Date(isoString);
  }

  /**
   * 判断字符串是否为有效的日期格式 YYYY-MM-DD
   * @param dateString 要检查的日期字符串
   * @returns 是否有效
   */
  static isValidDateString(dateString: string): boolean {
    if (!dateString) return false;

    // 检查格式是否为 YYYY-MM-DD
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;

    // 检查日期是否有效
    const date = new Date(dateString);
    const timestamp = date.getTime();
    if (isNaN(timestamp)) return false;

    return true;
  }

  /**
   * 计算两个日期之间的天数差
   * @param date1 第一个日期字符串 YYYY-MM-DD
   * @param date2 第二个日期字符串 YYYY-MM-DD
   * @returns 天数差
   */
  static daysBetween(date1: string, date2: string): number {
    if (!this.isValidDateString(date1) || !this.isValidDateString(date2)) {
      return 0;
    }

    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // 确保时间部分是一致的，只比较日期
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);

    // 计算差异（毫秒），然后转换为天
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  /**
   * 为日期添加指定的月数
   * @param dateString 日期字符串 YYYY-MM-DD
   * @param months 要添加的月数
   * @returns 新的日期字符串
   */
  static addMonths(dateString: string, months: number): string {
    if (!this.isValidDateString(dateString)) {
      return "";
    }

    const date = new Date(dateString);
    date.setMonth(date.getMonth() + months);
    return this.formatDate(date);
  }

  /**
   * 为日期添加指定的年数
   * @param dateString 日期字符串 YYYY-MM-DD
   * @param years 要添加的年数
   * @returns 新的日期字符串
   */
  static addYears(dateString: string, years: number): string {
    if (!this.isValidDateString(dateString)) {
      return "";
    }

    const date = new Date(dateString);
    date.setFullYear(date.getFullYear() + years);
    return this.formatDate(date);
  }

  /**
   * 将服务器返回的ISO日期时间格式化为本地易读格式
   * @param isoString ISO格式的日期字符串
   * @param includeTime 是否包含时间部分
   * @returns 格式化后的日期时间字符串
   */
  static formatISOToLocal(
    isoString: string,
    includeTime: boolean = true
  ): string {
    if (!isoString) return "-";

    try {
      const date = new Date(isoString);

      // 检查日期是否有效
      if (isNaN(date.getTime())) return "-";

      if (includeTime) {
        return this.formatDateTime(date);
      } else {
        return this.formatDate(date);
      }
    } catch (e) {
      console.error("日期格式化错误:", e);
      return "-";
    }
  }
}
